import { alpha } from '@mui/material/styles';
import { MODERN_FOCUS_STATES, MODERN_HOVER_EFFECTS, MODERN_SHADOWS, MODERN_TRANSITIONS } from '../styles/modernVariables';
import { getSharedInputStyles } from '../styles/sharedStyles';
import { ICustomTheme } from '../types/ICustomTheme';

export const getButtonStyles = () => ({
     styleOverrides: {
          root: ({ theme }: { theme: any }) => ({
               textTransform: 'none' as const,
               fontWeight: 500,
               borderRadius: '4px', // 4px conforme solicitado
               boxShadow: MODERN_SHADOWS.elevation[1],
               transition: MODERN_TRANSITIONS.smooth,
               position: 'relative' as const,
               overflow: 'hidden' as const,

               // Removendo animação automática que causava animações indesejadas em re-renders

               color:
                    theme.palette.mode === 'dark'
                         ? theme.palette.text.primary // Melhor contraste no dark
                         : theme.palette.text.secondary,

               // Garantir que o texto fique sempre visível
               '& .MuiButton-label, & > span': {
                    position: 'relative' as const,
                    zIndex: 2,
               }, // Efeito ripple moderno (atrás do texto) - adaptável ao tema
               '&::before': {
                    content: '""',
                    position: 'absolute' as const,
                    top: '50%',
                    left: '50%',
                    width: 0,
                    height: 0,
                    borderRadius: '4px', // Mesmo borderRadius do botão
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.08)',
                    transform: 'translate(-50%, -50%)',
                    transition: MODERN_TRANSITIONS.fast,
                    opacity: 0,
                    zIndex: -1,
                    pointerEvents: 'none' as const,
               }, // Hover moderno com elevação
               '&:hover': {
                    ...MODERN_HOVER_EFFECTS.lift,
                    color: theme.palette.text.primary,
                    '&::before': {
                         width: '120%',
                         height: '120%',
                         opacity: 1,
                    },
               },
               // Estado ativo suave
               '&:active': {
                    transform: 'translateY(-2px) scale(0.98)',
                    boxShadow: MODERN_SHADOWS.elevation[2],
               },
               // Focus state moderno
               '&:focus-visible': {
                    ...MODERN_FOCUS_STATES.ring(theme.palette.primary.main),
               }, // Variações por cor - contraste otimizado para cada tema
               '&.MuiButton-containedPrimary': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    color:
                         theme.palette.mode === 'dark'
                              ? '#FFFFFF' // Branco puro para máximo contraste no dark
                              : theme.palette.primary.contrastText,
                    '&:hover': {
                         background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                         boxShadow: MODERN_SHADOWS.glow.primary(theme.palette.primary.main),
                         color: theme.palette.mode === 'dark' ? '#FFFFFF' : theme.palette.primary.contrastText,
                    },
               },
               '&.MuiButton-containedSecondary': {
                    background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                    color:
                         theme.palette.mode === 'dark'
                              ? '#FFFFFF' // Branco puro para máximo contraste no dark
                              : theme.palette.secondary.contrastText,
                    '&:hover': {
                         background: `linear-gradient(135deg, ${theme.palette.secondary.dark} 0%, ${theme.palette.secondary.main} 100%)`,
                         boxShadow: MODERN_SHADOWS.glow.secondary(theme.palette.secondary.main),
                         color: theme.palette.mode === 'dark' ? '#FFFFFF' : theme.palette.secondary.contrastText,
                    },
               },
               '&.MuiButton-contained': {
                    // Para botões contained genéricos, usar cor com bom contraste
                    color:
                         theme.palette.mode === 'dark'
                              ? '#FFFFFF' // Branco puro no dark para melhor contraste
                              : theme.palette.getContrastText?.(theme.palette.grey[500]) || theme.palette.text.primary,
                    '&:hover': {
                         color: theme.palette.mode === 'dark' ? '#FFFFFF' : theme.palette.getContrastText?.(theme.palette.grey[600]) || theme.palette.text.primary,
                    },
               },
               '&.MuiButton-outlined': {
                    borderWidth: '1.5px',
                    color:
                         theme.palette.mode === 'dark'
                              ? theme.palette.text.primary // text.primary tem melhor contraste no dark
                              : theme.palette.text.secondary,
                    '&:hover': {
                         borderWidth: '1.5px',
                         backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.04),
                         color: theme.palette.mode === 'dark' ? '#FFFFFF' : theme.palette.text.primary,
                    },
               },
               '&.MuiButton-text': {
                    color:
                         theme.palette.mode === 'dark'
                              ? theme.palette.text.primary // text.primary tem melhor contraste no dark
                              : theme.palette.text.secondary,
                    '&:hover': {
                         color: theme.palette.mode === 'dark' ? '#FFFFFF' : theme.palette.text.primary,
                    },
               },

               // Keyframes de animação
               '@keyframes buttonFadeIn': {
                    '0%': {
                         opacity: 0,
                         transform: 'scale(0.95) translateY(5px)',
                    },
                    '100%': {
                         opacity: 1,
                         transform: 'scale(1) translateY(0)',
                    },
               },
          }),
     },
     defaultProps: {
          disableElevation: false,
          disableRipple: false,
     },
});

export const getTextFieldStyles = (themeOptions: ICustomTheme) => {
     const sharedInputStyles = getSharedInputStyles(themeOptions);

     return {
          defaultProps: {
               variant: 'outlined' as const,
               size: 'small' as const,
          },
          styleOverrides: {
               root: ({ theme }: { theme: any }) => ({
                    ...sharedInputStyles,
                    '& .MuiInputBase-root': {
                         ...sharedInputStyles['& .MuiInputBase-root'],
                         borderRadius: '4px', // 4px conforme solicitado                         transition: MODERN_TRANSITIONS.smooth,
                         position: 'relative' as const,

                         // Removendo animação automática que causava animações indesejadas em re-renders

                         // Glassmorphism sutil
                         backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.6) : alpha(theme.palette.background.paper, 0.8),
                         backdropFilter: 'blur(8px)',

                         '&:hover': {
                              ...MODERN_HOVER_EFFECTS.lift,
                              backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.8) : alpha(theme.palette.background.paper, 0.95),
                         },

                         '&.Mui-focused': {
                              transform: 'translateY(-1px)',
                              boxShadow: MODERN_SHADOWS.glow.primary(theme.palette.primary.main),
                              backgroundColor: theme.palette.background.paper,

                              '& .MuiOutlinedInput-notchedOutline': {
                                   borderWidth: '2px',
                                   borderColor: theme.palette.primary.main,
                              },
                         },

                         // Estados de erro com animação
                         '&.Mui-error': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                   borderColor: theme.palette.error.main,
                                   animation: 'errorPulse 0.6s ease-in-out',
                              },
                         },
                    },
                    // Animação de erro
                    '@keyframes errorPulse': {
                         '0%, 100%': {
                              borderColor: theme.palette.error.main,
                              boxShadow: 'none',
                         },
                         '50%': {
                              borderColor: theme.palette.error.main,
                              boxShadow: `0 0 10px ${alpha(theme.palette.error.main, 0.3)}`,
                         },
                    },

                    // Animação de entrada para inputs
                    '@keyframes inputFadeIn': {
                         '0%': {
                              opacity: 0,
                              transform: 'translateY(10px)',
                         },
                         '100%': {
                              opacity: 1,
                              transform: 'translateY(0)',
                         },
                    },

                    // Label moderno
                    '& .MuiInputLabel-root': {
                         fontWeight: 500,
                         '&.Mui-focused': {
                              fontWeight: 600,
                         },
                    },
               }),
          },
     };
};

export const getSelectStyles = (themeOptions: ICustomTheme) => {
     const sharedInputStyles = getSharedInputStyles(themeOptions);

     return {
          defaultProps: {
               size: 'small' as const,
          },
          styleOverrides: {
               root: {
                    ...sharedInputStyles,
                    '&:hover .MuiInputBase-root': {
                         boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
                    },
               },
               select: {
                    paddingTop: '8.5px',
                    paddingBottom: '8.5px',
                    paddingLeft: '14px',
                    paddingRight: '32px', // Espaço para o ícone dropdown
               },
          },
     };
};

export const getAutocompleteStyles = (themeOptions: ICustomTheme) => {
     const sharedInputStyles = getSharedInputStyles(themeOptions);

     return {
          defaultProps: {
               size: 'small' as const,
          },
          styleOverrides: {
               root: {
                    ...sharedInputStyles,
                    '&:hover .MuiInputBase-root': {
                         boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
                    },
               },
               input: {
                    color: themeOptions.palette.text.primary,
               },
               paper: {
                    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
                    backgroundColor: themeOptions.palette.background.paper,
                    color: themeOptions.palette.text.primary,
               },
               listbox: {
                    '& .MuiAutocomplete-option': {
                         color: themeOptions.palette.text.primary,
                         '&[aria-selected="true"]': {
                              backgroundColor: alpha(themeOptions.palette.primary.main, 0.2),
                         },
                         '&.Mui-focused': {
                              backgroundColor: alpha(themeOptions.palette.primary.main, 0.1),
                         },
                    },
               },
          },
     };
};
